import Loading from '@common/components/Loading';
import { bannerFullScreen } from '@common/constants/admob';
import { admobSelector } from '@common/redux/selectors/admob';
import ObjectRender from '@common/screens/RenderScreen/ObjectRender';
import { IRecord } from '@common/types/';
import { IList } from '@common/types/element';
import { getValueBindingComponent } from '@common/utils/component';
import { get, isEmpty, max, uniqBy } from 'lodash';
import React, { FC, useMemo, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  FlatList,
  Platform,
  Text,
  TouchableOpacity,
  View,
  ViewToken,
} from 'react-native';
import { useSelector } from 'react-redux';
import EmptyList from '../../EmptyList';
import Item from './item';
import createStyle from './style';

const CusList: FC<IList> = (attrs) => {
  const {
    children,
    rowMargin,
    admobs,
    dataBinding,
    page,
    totalPage,
    onLoadMore,
    initializeList,
    isLoadMore,
  } = attrs;
  const styles = createStyle(attrs, dataBinding);
  const tableId = get(attrs, 'databaseOptions.tableId');
  const horizontalScroll = get(
    attrs,
    'databaseOptions.options.horizontalScroll'
  );
  const activeSliderAnimation = get(
    attrs,
    'databaseOptions.options.activeSliderAnimation',
    false
  );

  const { width } = Dimensions.get('screen');
  const [contentSizeWidth, setContentSizeWidth] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const flatListRef = useRef<FlatList>(null);
  const scaleAnim = useRef(new Animated.Value(1)).current;

  // Keep refs for consistency but don't use them for auto-activation
  const viewabilityConfigRef = useRef({
    itemVisiblePercentThreshold: 50,
    minimumViewTime: 0,
  });

  const textEmpty = useMemo(
    () => getValueBindingComponent(attrs, 'defaultText'),
    []
  );
  const { isUseAdmob } = useSelector(admobSelector);

  const renderTextEmpty = useMemo(() => {
    return (
      <View
        style={[
          styles.cusListWrapper,
          {
            alignItems: 'center',
            justifyContent: 'center',
          },
        ]}
      >
        <Text style={{ fontSize: 22, fontWeight: '500' }}>{textEmpty}</Text>
      </View>
    );
  }, [textEmpty]);

  // User cannot select table
  if (isEmpty(tableId)) {
    return (
      <View style={styles.emptyListWrapper}>
        <EmptyList attributes={attrs} />
      </View>
    );
  }

  if (initializeList) {
    return <Loading />;
  }

  if (dataBinding.length <= 0) return renderTextEmpty;

  const paginateArr = get(attrs, 'databaseOptions.options.paginate', false);

  if (children.length == 0) return renderTextEmpty;

  const getAdmobComponent = (
    index: number,
    attrs: React.PropsWithChildren<IList>
  ) => {
    if (isUseAdmob && admobs?.length) {
      const admobListComponent = [];
      for (let adIndex = 0; adIndex < admobs.length; adIndex++) {
        if (Platform.OS === 'web' && !admobs[adIndex].placeholder) {
          break;
        }

        if (
          index !== undefined &&
          (index + 1) % admobs[adIndex].perItem === 0
        ) {
          let width: number = 0,
            height: number = 0;
          const isAdFullScreen = bannerFullScreen.includes(
            admobs[adIndex].bannerSize
          );
          if (!isAdFullScreen) {
            const columnCount = attrs.columnCount || 1;
            width = admobs[adIndex].width / columnCount;
            height = (admobs[adIndex].height / admobs[adIndex].width) * width;
          }

          admobListComponent.push(
            <ObjectRender
              keyItem={index}
              arrComp={[
                {
                  ...admobs[adIndex],
                  width,
                  height,
                  hidden: isAdFullScreen,
                },
              ]}
              isScreen={false}
            />
          );
        }
      }

      return (
        <View
          nativeID={`custom-list-item-element-${attrs.id}-${index}`}
          style={{
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: rowMargin,
          }}
        >
          {admobListComponent}
        </View>
      );
    }
    return null;
  };

  const ListFooterComponent = () => {
    return (
      <TouchableOpacity
        onPress={() => {
          if (totalPage > page) {
            onLoadMore();
          }
        }}
      >
        {isLoadMore ? (
          <Loading />
        ) : (
          <Text style={styles.loadMoreText}>もっとロード</Text>
        )}
      </TouchableOpacity>
    );
  };

  const handleItemPress = (index: number) => {
    if (horizontalScroll && activeSliderAnimation) {
      setActiveIndex(index);
      // Scroll to the item to center it
      if (flatListRef?.current) {
        flatListRef.current.scrollToIndex({
          index,
          animated: true,
          viewPosition: 0.5,
        });
      }
    }
  };

  const renderItemList = ({ item, index }: any) => {
    const isHorizontalWithSlider = horizontalScroll && activeSliderAnimation;
    const isActive = isHorizontalWithSlider && index === activeIndex;
    const isHovered = isHorizontalWithSlider && index === hoveredIndex;

    if (isUseAdmob && admobs?.length) {
      return getAdmobComponent(index, attrs);
    }

    const itemContent = (
      <Item
        nativeID={`custom-list-item-element-${attrs.id}-${index}`}
        key={index}
        item={item}
        attrs={attrs}
        index={index}
        isActive={isActive}
      />
    );

    if (isHorizontalWithSlider) {
      const scale = isActive ? 1.15 : 0.9;
      const opacity = isActive ? 1 : isHovered ? 0.8 : 0.6;

      return (
        <View
          style={{
            paddingVertical: 20,
          }}
          onStartShouldSetResponder={() => true}
          onMoveShouldSetResponder={() => false}
          onResponderGrant={() => {
            setHoveredIndex(index);
          }}
          onResponderRelease={() => {
            setHoveredIndex(null);
            handleItemPress(index);
          }}
          onResponderTerminate={() => {
            setHoveredIndex(null);
          }}
          {...(Platform.OS === 'web' && {
            onMouseEnter: () => {
              setHoveredIndex(index);
            },
            onMouseLeave: () => {
              setHoveredIndex(null);
            },
            onMouseDown: () => {
              handleItemPress(index);
            },
          })}
        >
          <Animated.View
            style={{
              transform: [{ scale }],
              opacity,
            }}
          >
            {itemContent}
          </Animated.View>
        </View>
      );
    }

    return itemContent;
  };

  const keyExtractor = (item: IRecord) => item?._id || item.id;

  const isShowFooterFLatList = paginateArr && totalPage > page;
  const isHorizontalWithSlider = horizontalScroll && activeSliderAnimation;

  return (
    <View
      style={styles.cusListWrapper}
      nativeID={`custom-list-element-${attrs.id}`}
    >
      {dataBinding?.length ? (
        <FlatList
          ref={flatListRef}
          {...(isShowFooterFLatList && {
            ListFooterComponent: <ListFooterComponent />,
          })}
          horizontal={horizontalScroll}
          {...(!horizontalScroll && { numColumns: attrs.columnCount })}
          key={attrs.columnCount}
          showsHorizontalScrollIndicator={Platform.OS === 'web' ? true : false}
          style={{
            marginLeft:
              attrs.style?.gap !== undefined ? attrs.style?.gap * -1 : -8,
            marginRight:
              attrs.style?.gap !== undefined ? attrs.style?.gap * -1 : -8,
          }}
          contentContainerStyle={
            isHorizontalWithSlider
              ? {
                  paddingVertical: 30,
                  paddingHorizontal: 10,
                }
              : undefined
          }
          data={uniqBy(dataBinding, keyExtractor)}
          showsVerticalScrollIndicator={true}
          keyExtractor={keyExtractor}
          renderItem={renderItemList}
          onEndReachedThreshold={0.1}
          {...(!horizontalScroll && attrs?.columnCount && attrs?.columnCount > 1
            ? {
                columnWrapperStyle: {
                  justifyContent: 'space-between',
                },
              }
            : {})}
          onScrollEndDrag={() => {
            attrs?.setEnableScrollViewScroll &&
              attrs?.setEnableScrollViewScroll(true);
          }}
          onTouchStart={() => {
            if (contentSizeWidth > width) {
              attrs?.setEnableScrollViewScroll &&
                attrs?.setEnableScrollViewScroll(false);
            }
          }}
          onContentSizeChange={(width, height) => {
            setContentSizeWidth(width);
          }}
          {...(isHorizontalWithSlider && {
            snapToAlignment: 'center',
            snapToInterval: attrs.width + (attrs.rowMargin || 0),
            decelerationRate: 'fast',
            pagingEnabled: false,
          })}
        />
      ) : null}
    </View>
  );
};

export default CusList;
